import React, { useState } from "react";
import { Helmet } from "react-helmet";
import ScrollToTop from "../../../../../components/scrollToTop/scrollToTop";
import Swal from "sweetalert2";
import "./quoter.css";

// Función para convertir número a letras en español
function numberToSpanishWords(amount) {
  const UNITS = [
    "",
    "UN",
    "DOS",
    "TRES",
    "CUATRO",
    "CINCO",
    "SEIS",
    "SIETE",
    "OCHO",
    "NUEVE",
    "DIEZ",
    "ONCE",
    "DOCE",
    "TRECE",
    "CATORCE",
    "QUINCE",
    "DIECISÉIS",
    "DIECISIETE",
    "DIECIOCHO",
    "DIECINUEVE",
    "VEINTE",
  ];
  const TENS = [
    "",
    "",
    "VEINTE",
    "TREINTA",
    "CUARENTA",
    "CINCUENTA",
    "SESENTA",
    "SETENTA",
    "OCHENTA",
    "NOVENTA",
  ];
  const HUNDREDS = [
    "",
    "CIENTO",
    "DOSCIENTOS",
    "TRESCIENTOS",
    "CUATROCIENTOS",
    "QUINIENTOS",
    "SEISCIENTOS",
    "SETECIENTOS",
    "OCHOCIENTOS",
    "NOVECIENTOS",
  ];

  function convertGroup(n) {
    if (n === 100) return "CIEN";
    let output = "";
    if (n >= 100) {
      output += HUNDREDS[Math.floor(n / 100)] + " ";
      n %= 100;
    }
    if (n <= 20) {
      output += UNITS[n];
    } else {
      output += TENS[Math.floor(n / 10)];
      if (n % 10 !== 0) {
        output += " Y " + UNITS[n % 10];
      }
    }
    return output.trim();
  }

  function splitNumber(n) {
    const parts = [];
    while (n > 0) {
      parts.unshift(n % 1000);
      n = Math.floor(n / 1000);
    }
    return parts;
  }

  const integerPart = Math.floor(amount);
  const decimalPart = Math.round((amount - integerPart) * 100);

  const parts = splitNumber(integerPart);
  const names = ["", "MIL", "MILLONES"];
  let result = "";

  parts.forEach((group, i) => {
    if (group > 0) {
      const name = names[parts.length - i - 1];
      result += convertGroup(group) + (name ? " " + name : "") + " ";
    }
  });

  const pesos = result.trim();
  const centavos = convertGroup(decimalPart);

  return `${pesos} PESOS CON ${centavos} CENTAVOS / 100 M.N.`.toUpperCase();
}

function Quoter() {
  const [cantidades, setCantidades] = useState(Array(8).fill(0));
  const [formData, setFormData] = useState({
    pesoCarga: 0,
    pesoRefrigeracion: 0,
    pesoCongelacion: 0,
    valorAduana: 0,
    unPeligrosas: 0,
    contenedorAVI: 0,
    hieloSeco: false,
    dgExentas: false,
    fechaEntrada: "",
    horaEntrada: "",
    fechaSalida: "",
    horaSalida: "",
  });
  const [alertaVisible, setAlertaVisible] = useState(false);

  const precios = {
    "Manejo de carga": 259.5,
    Screening: 0.91,
    Refrigeración: 389.24,
    Congelación: 583.87,
    "Manejo por valor": 179.05,
    "DG Fee": 1427.23,
    "Animales vivos": 454.12,
    "Hielo seco": 389.24,
    "DG exentas": 778.49,
    "Fleje y empaque": [
      454.12, 324.37, 155.7, 103.8, 84.34, 58.39, 467.09, 381.46,
    ],
  };

  const handleCantidadChange = (index, value) => {
    const updatedCantidades = [...cantidades];
    updatedCantidades[index] = Math.max(0, parseInt(value) || 0);
    setCantidades(updatedCantidades);
  };

  const imprimirTicketCotizador = () => {
    // Validar que los campos de fecha y hora estén llenos
    if (
      !formData.fechaEntrada ||
      !formData.horaEntrada ||
      !formData.fechaSalida ||
      !formData.horaSalida
    ) {
      Swal.fire({
        icon: "warning",
        title: "Campos obligatorios faltantes",
        html: `
        <p style="text-align:left;"><strong>Favor de llenar los campos obligatorios para continuar:</strong></p>
        <ul style="text-align:left;">
          <li>Fecha entrada</li>
          <li>Hora entrada</li>
          <li>Fecha salida</li>
          <li>Hora salida</li>
        </ul>
      `,
        confirmButtonText: "Entendido",
      });
      return;
    }

    // Validar peso de carga
    if (formData.pesoCarga <= 0) {
      setAlertaVisible(true);
      return;
    }

    setAlertaVisible(false);
    const now = new Date().toLocaleString("es-MX");

    const detalles = [
      { concepto: "Manejo de carga", monto: precios["Manejo de carga"] },
      {
        concepto: "Refrigeración",
        monto:
          formData.pesoRefrigeracion > 0
            ? precios["Refrigeración"] * formData.pesoRefrigeracion
            : 0,
      },
      {
        concepto: "Congelación",
        monto:
          formData.pesoCongelacion > 0
            ? precios["Congelación"] * formData.pesoCongelacion
            : 0,
      },
      {
        concepto: "Screening",
        monto:
          formData.pesoCarga > 0
            ? formData.pesoCarga * precios["Screening"]
            : 0,
      },
      {
        concepto: "DG Fee",
        monto: formData.unPeligrosas > 0 ? precios["DG Fee"] : 0,
      },
      {
        concepto: "Manejo por valor",
        monto: formData.valorAduana > 0 ? precios["Manejo por valor"] : 0,
      },
      {
        concepto: "Fleje y empaque",
        monto: cantidades.reduce(
          (acc, val, i) =>
            acc + (val > 0 ? precios["Fleje y empaque"][i] * val : 0),
          0
        ),
      },
      {
        concepto: "Animales vivos",
        monto: formData.contenedorAVI > 0 ? precios["Animales vivos"] : 0,
      },
      {
        concepto: "DG exentas",
        monto: formData.dgExentas ? precios["DG exentas"] : 0,
      },
      {
        concepto: "Hielo seco",
        monto: formData.hieloSeco ? precios["Hielo seco"] : 0,
      },
    ];

    const subtotal = detalles.reduce((acc, item) => acc + item.monto, 0);

    // IVA como porcentaje general
    const tasaIVA = 0.16;
    const iva = subtotal * tasaIVA;

    const total = subtotal + iva;
    const totalEnLetra = numberToSpanishWords(total);

    const tableRows = detalles
      .map(
        (item) => `
      <tr>
        <td style="text-align: start;">${item.concepto}</td>
        <td style="text-align: center;">${
          item.monto > 0 ? `$${item.monto.toFixed(2)}` : "0.00"
        }</td>
      </tr>
    `
      )
      .join("");

    const htmlContent = `
    <div id="ticketContent" style="font-family: Arial; padding: 10px;">
      <h3 class="no-print" style="text-align: start;"><strong>Ticket de cotización</strong></h3>
      <div style="text-align: center;"><img src="/img-Ticket-Billing/logo-azul.svg" style="height: 60px;" /></div>
      <hr><p style="text-align: center;">Aerovías de México S.A de C.V.</p>
      <p style="text-align: center;">AME123456I89</p>
      <p style="text-align: center;">Col. Cuauhtémoc, C.P. 06500, Ciudad de México</p>
      <p style="text-align: center;">Paseo de la Reforma 243 Piso 25</p>
      <hr><p style="text-align: center;">Cotizado el día: ${now}</p><hr>
      <table class="table fondo-headBillingMonto">
        <thead><tr><th>Cargo</th><th>Importe</th></tr></thead>
        <tbody>
          ${tableRows}
          <tr><td style="font-weight: bold; text-align: center;">Subtotal</td><td style="text-align: center;">$${subtotal.toFixed(
            2
          )}</td></tr>
          <tr><td style="font-weight: bold; text-align: center;">IVA</td><td style="text-align: center;">$${iva.toFixed(
            2
          )}</td></tr>
          <tr><td style="font-weight: bold; text-align: center;">Total</td><td style="text-align: center;">$${total.toFixed(
            2
          )}</td></tr>
        </tbody>
      </table>
      <hr><p style="text-align: left; text-transform: uppercase;"><small>(${totalEnLetra})</small></p>
      <p class="text-start"><strong>Este documento es una estimación conforme a los datos que usted ingresó.</strong></p>
      <p class="text-start"><strong>El cálculo que se presenta está en función de nuestra tarifa pública.</strong></p>
      <p class="text-start"><strong>La cotización puede variar en función del peso, valor de aduana y manejo especial que pueda requerir su embarque al momento de ingresarse al almacén.</strong></p>
      <p class="text-start"><strong>* Precios sujetos a cambio sin previo aviso.</strong></p>
      <div style="text-align: right; margin-top: 30px;">
        <button id="btn-imprimir-ticket" class="btnn">Imprimir</button>
      </div>
    </div>
  `;

    Swal.fire({
      html: htmlContent,
      showConfirmButton: false,
      customClass: { popup: "ticket-modal" },
      width: 500,
      didOpen: () => {
        const btn = document.getElementById("btn-imprimir-ticket");
        btn?.addEventListener("click", () => {
          const content = document.getElementById("ticketContent").innerHTML;
          const win = window.open("", "", "width=800,height=900");
          win.document.write(
            `<html><head><title>Imprimir</title></head><body>${content}</body></html>`
          );
          win.document.close();
          win.print();
          win.close();
        });
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>Horus | Cotizador</title>
      </Helmet>
      <ScrollToTop />

      <div className="container-fluid">
        {/* Formulario */}
        <div className="row">
          {[
            ["Fecha entrada *", "fechaEntrada", "date"],
            ["Hora entrada *", "horaEntrada", "time"],
            ["Fecha salida *", "fechaSalida", "date"],
            ["Hora salida *", "horaSalida", "time"],
          ].map(([label, name, type]) => (
            <div className="col-md-3 mb-3" key={name}>
              <label className="fw-bold">{label}</label>
              <input
                type={type}
                className="form-control"
                value={formData[name]}
                onChange={(e) =>
                  setFormData({ ...formData, [name]: e.target.value })
                }
              />
            </div>
          ))}
        </div>

        <div className="row">
          {[
            ["Peso de la carga *", "pesoCarga"],
            ["Peso refrigeración", "pesoRefrigeracion"],
            ["Peso congelación", "pesoCongelacion"],
            ["Valor aduana", "valorAduana"],
          ].map(([label, name]) => (
            <div className="col-md-3 mb-3" key={name}>
              <label className="fw-bold">{label}</label>
              <input
                min="0"
                step="any"
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e") {
                    e.preventDefault();
                  }
                }}
                type="number"
                className="form-control"
                value={formData[name]}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    [name]: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </div>
          ))}
        </div>

        <div className="row">
          <div className="col-md-3 mb-3">
            <label className="fw-bold">UN (Merc. peligrosas)</label>
            <input
              type="number"
              className="form-control"
              value={formData.unPeligrosas}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  unPeligrosas: parseFloat(e.target.value) || 0,
                })
              }
            />
            <label className="merc-peligrosas">
              Cantidad total de UN en la guía
            </label>
          </div>

          <div className="col-md-3 mb-3">
            <label className="fw-bold">Contenedor de AVI</label>
            <input
              type="number"
              className="form-control"
              value={formData.contenedorAVI}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  contenedorAVI: parseFloat(e.target.value) || 0,
                })
              }
            />
          </div>

          <div className="col-md-3 mb-3">
            <label className="fw-bold d-block">¿Hielo Seco?</label>
            <input
              type="checkbox"
              className="form-check-input mt-1"
              checked={formData.hieloSeco}
              onChange={(e) =>
                setFormData({ ...formData, hieloSeco: e.target.checked })
              }
            />
          </div>

          <div className="col-md-3 mb-3">
            <label className="fw-bold d-block">¿DG exentas?</label>
            <input
              type="checkbox"
              className="form-check-input mt-1"
              checked={formData.dgExentas}
              onChange={(e) =>
                setFormData({ ...formData, dgExentas: e.target.checked })
              }
            />
          </div>
        </div>

        {/* Tabla de cantidades */}
        <div className="row mt-3 mb-4">
          <div className="col-12 d-flex justify-content-center">
            <table className="table fondo-headBilling text-center w-50">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {[
                  "Fleje skid 1.60 cm",
                  "Fleje skid medio",
                  "Fleje pza suelta",
                  "Vitafilm skid 1.60 cm",
                  "Vitafilm skid medio",
                  "Vitafilm pza suelta",
                  "Suministro Tarima Std",
                  "Suministro Tarima Eur",
                ].map((nombre, i) => (
                  <tr key={i}>
                    <td>{nombre}</td>
                    <td>
                      <input
                        type="number"
                        className="form-control text-center"
                        value={cantidades[i]}
                        onChange={(e) =>
                          handleCantidadChange(i, e.target.value)
                        }
                        min="0"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {alertaVisible && (
          <div className="row">
            <div className="col-12 text-center">
              <div className="alert alert-danger">
                El campo <strong>"Peso de la carga"</strong> debe ser mayor a 0
              </div>
            </div>
          </div>
        )}

        <div className="row">
          <div className="btns mt-3 d-flex justify-content-end gap-3">
            <button className="btnn" onClick={imprimirTicketCotizador}>
              Cotizar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default Quoter;
